import { faPlus, faTrash, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useState } from "react";
import { Button } from "react-bootstrap";

import { RIPropertiesOnRentDetailsForm } from ".";
import { RegimeTypes } from "../../../config/filingConstants";
import { IFormITR1, IGivenOnRentProperties, IReviewHPGains } from "../../../config/interfaces";
import FASvgIcon from "../../FASvgIcon";
import PreventEditComponent from "../../PreventEditComponent";
import PriceDisplay from "../../PriceDisplay";
import { ConfirmationModal } from "../../modals";

const RIPropertiesOnRentDetails: React.FC = () => {
	const [showAddFormCanvas, setShowAddFormCanvas] = useState<boolean>(false);
	const [showDeleteModal, setShowDeleteModal] = useState<IGivenOnRentProperties | null>(null);
	const [activeItem, setActiveItem] = useState<IGivenOnRentProperties | null>(null);
	// used in both edit and delete cases
	const [activeItemIdx, setActiveItemIdx] = useState<string>("");

	const { values, setFieldValue, setValues } = useFormikContext<IFormITR1>();

	const RegimeType = values?.FilingReturnInfo.RegimeType || RegimeTypes.NEW_TAX_REGIME;
	const PropertiesOnRentDetails = cloneDeep(values?.IncomeDetails?.RIGivenOnRentProperties || []);

	const handleDeleteInterestItem = async () => {
		PropertiesOnRentDetails.splice(Number(activeItemIdx), 1);
		setFieldValue("IncomeDetails.RIGivenOnRentProperties", PropertiesOnRentDetails);
		setShowDeleteModal(() => null);
		setActiveItem(() => null);
		setActiveItemIdx(() => "");
		handleTotalCalculation();
	};

	const handleTotalCalculation = () => {
		const OnRent =
			PropertiesOnRentDetails?.reduce((acc, curr) => {
				const value = Number(curr.NetTaxableIncome);
				return acc + (isNaN(value) ? 0 : value);
			}, 0) || 0;

		const _HousePropertyIncomes: IReviewHPGains = {
			...values.HousePropertyIncomes,
			TotalOnRent: OnRent
		};

		let _TotalHousePropertyIncome = 0;
		const _SelfOccupied = values.HousePropertyIncomes.TotalSelfOccupied;
		if (RegimeType === RegimeTypes.OLD_TAX_REGIME) {
			_TotalHousePropertyIncome += _SelfOccupied + OnRent;
		} else if (RegimeType === RegimeTypes.NEW_TAX_REGIME && OnRent > 0) {
			_TotalHousePropertyIncome += OnRent;
		}

		if (_TotalHousePropertyIncome < 0) {
			_TotalHousePropertyIncome = Math.max(_TotalHousePropertyIncome, -200000);
		}

		const DATA = {
			HousePropertyIncomes: _HousePropertyIncomes,
			TotalHousePropertyIncome: _TotalHousePropertyIncome
		};

		setValues({ ...cloneDeep(values), ...DATA });
		setFieldValue("IncomeDetails.RIGivenOnRentProperties", PropertiesOnRentDetails);
	};

	return (
		<>
			<section>
				<div className="section-title-wrap">
					<h5>
						Properties given on rent
						<small>Add rent, address, loan interest etc. for properties given on rent</small>
					</h5>
					<PreventEditComponent>
						<Button variant="secondary" size="sm" onClick={() => setShowAddFormCanvas(() => true)}>
							<FASvgIcon icon={faPlus} />
							Add
						</Button>
					</PreventEditComponent>
				</div>

				<dl className="eds-list eds-dl eds-inline-between">
					{PropertiesOnRentDetails?.map((property, index) => {
						return (
							<div key={`any-asset-${index}`} className="eds-list--item">
								<dt>
									{index + 1}. {property?.PropertyType}
								</dt>
								<dd>
									<span>
										<PriceDisplay price={property.NetTaxableIncome || 0} allowNegative={true} />
									</span>
									<Button
										variant="link"
										onClick={() => {
											setShowAddFormCanvas(() => true);
											setActiveItem(() => property);
											setActiveItemIdx(() => String(index));
										}}
									>
										View
										<PreventEditComponent>
											<>/Edit</>
										</PreventEditComponent>
									</Button>
									<PreventEditComponent>
										<Button
											variant="transparent"
											className="icon-only"
											onClick={() => {
												setShowDeleteModal(() => property);
												setActiveItemIdx(() => String(index));
											}}
										>
											<FASvgIcon icon={faTrash} />
										</Button>
									</PreventEditComponent>
								</dd>
							</div>
						);
					})}
				</dl>
			</section>

			<RIPropertiesOnRentDetailsForm
				showCanvas={showAddFormCanvas}
				handleCloseBtnPress={(rentProData) => {
					if (rentProData) {
						if (activeItem) {
							PropertiesOnRentDetails.splice(Number(activeItemIdx), 1, rentProData);
						} else {
							PropertiesOnRentDetails.push(rentProData);
						}
						handleTotalCalculation();
					}
					setShowAddFormCanvas(() => false);
					setActiveItem(() => null);
					setActiveItemIdx(() => "");
				}}
				formValues={activeItem}
			/>

			<ConfirmationModal
				showModal={!!showDeleteModal}
				iconColorClass="text-danger"
				title="Are you sure?"
				message={<p>You want to delete this</p>}
				onYesBtnClick={() => handleDeleteInterestItem()}
				onNoBtnClick={() => setShowDeleteModal(() => null)}
				icon={faTrashAlt}
			/>
		</>
	);
};

export default RIPropertiesOnRentDetails;
